import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBlogById } from '../store/slices/blogSlice';
import SEO from '../components/SEO/SEO';
import { generateArticleSchema, generateBreadcrumbSchema } from '../utils/structuredData';
import { fetchSEO } from '../store/slices/seoSlice';
import { FaArrowLeft } from 'react-icons/fa';

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBlog, loading } = useSelector((state) => state.blog);
  const settings = useSelector((state) => state.settings.data);

  useEffect(() => {
    dispatch(fetchBlogById(id));
    dispatch(fetchSEO('blog-detail'));
  }, [id, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading && !currentBlog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-96 bg-gray-200 rounded mb-8" />
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!currentBlog) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 mb-4">Blog post not found</p>
        <button
          onClick={() => navigate('/blog')}
          className="text-primary hover:underline"
        >
          Back to Blog
        </button>
      </div>
    );
  }

  const structuredData = [
    generateArticleSchema(currentBlog, settings),
    generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Blog', url: '/blog' },
      { name: currentBlog.title, url: `/blog/${currentBlog._id}` },
    ]),
  ];

  return (
    <>
      <SEO
        page="blog-detail"
        title={currentBlog.title}
        description={currentBlog.excerpt || currentBlog.title}
        keywords={`${currentBlog.category}, ${currentBlog.tags?.join(', ') || ''}, blog, art, vastu`}
        image={currentBlog.image}
        url={`/blog/${currentBlog._id}`}
        type="article"
        publishedTime={currentBlog.publishedAt || currentBlog.createdAt}
        modifiedTime={currentBlog.updatedAt || currentBlog.publishedAt || currentBlog.createdAt}
        structuredData={structuredData}
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition"
      >
        <FaArrowLeft />
        Back
      </button>

      <article className="bg-white rounded-lg shadow-md overflow-hidden">
        {currentBlog.image && (
          <div className="relative h-96 overflow-hidden">
            <img
              src={currentBlog.image}
              alt={currentBlog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-8">
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-secondary-light text-primary rounded-full text-sm">
              {currentBlog.category}
            </span>
          </div>

          <h1 className="font-heading text-4xl font-bold text-black mb-4">
            {currentBlog.title}
          </h1>

          <div className="flex items-center gap-4 text-gray-600 mb-6 pb-6 border-b">
            <span>By {currentBlog.author || 'Admin'}</span>
            <span>•</span>
            <span>{formatDate(currentBlog.publishedAt || currentBlog.createdAt)}</span>
            {currentBlog.views > 0 && (
              <>
                <span>•</span>
                <span>{currentBlog.views} views</span>
              </>
            )}
          </div>

          {currentBlog.excerpt && (
            <p className="text-xl text-gray-700 mb-6 italic">
              {currentBlog.excerpt}
            </p>
          )}

          <div className="prose max-w-none mb-6">
            <div 
              className="text-gray-700 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: currentBlog.content }}
            />
          </div>

          {currentBlog.tags && currentBlog.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="font-semibold mb-3 text-black">Tags:</h3>
              <div className="flex flex-wrap gap-2">
                {currentBlog.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t">
            <Link
              to="/blog"
              className="text-primary hover:underline flex items-center gap-2"
            >
              <FaArrowLeft />
              Back to Blog
            </Link>
          </div>
        </div>
      </article>
      </div>
    </>
  );
};

export default BlogDetailPage;

